import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import obelisk from '../clients/obelisk';
import { getMDYDate } from './Helper';
import { SubscriptionProductsGetService } from '../services/products.service';

/**
 * Custom hook to manage subscription-related functionality.
 *
 * @returns {{
 *   loading: boolean,
 *   expirationDate: string | null,
 *   setCustomerId: React.Dispatch<React.SetStateAction<string | null>>,
 *   subscriptionName: React.Dispatch<React.SetStateAction<string | null>>,
 *   setSubscriptions: React.Dispatch<React.SetStateAction<Array<Object>>>,
 *   subscriptions: Array<Object>,
 *   setErrorMsg: React.Dispatch<React.SetStateAction<string>>,
 *   isExpired: boolean
 * }} SubscriptionServiceHook
 */
const useSubscribeService = (cid, customerSubscriptions) => {
  const [loading, setLoading] = useState(false);
  const [customerId, setCustomerId] = useState(cid || null);
  const [subscriptionName, setSubscriptionName] = useState(null);
  const [subscriptionId, setSubscriptionId] = useState(null);
  const [subscriptions, setSubscriptions] = useState(customerSubscriptions || []);
  const [subscriptionProducts, setSubscriptionProducts] = useState([]);
  const [expirationDate, setExpirationDate] = useState(null);
  const [errorMsg, setErrorMsg] = useState('There was an error activating the service');
  const [isExpired, setIsExpired] = useState(true);
  const [refreshSubscriptions, setRefreshSubscriptions] = useState(
    customerSubscriptions?.length === 0
  );
  // console.log(subscriptions, 'subs');
  /**
   * Fetches subscriptions available.
   */
  const getProducts = async () => {
    try {
      const resp = await SubscriptionProductsGetService();
      setSubscriptionProducts(resp?.data || []);
      return resp?.data || [];
    } catch (error) {
      console.error('Error in getProducts:', error);
    }
  };
  /**
   * Fetches the distributors active subscriptions.
   */
  const getAssociateSubscription = async () => {
    if (subscriptions.length > 0) {
      if (!refreshSubscriptions) {
        return;
      }
    }
    setLoading(true);
    try {
      const resp = await obelisk().get('Subscription/GetAssociateSubscription', {
        associateId: customerId,
      });
      setSubscriptions(resp.data || []);
      setLoading(false);
      setRefreshSubscriptions(false);
    } catch (error) {
      console.error('Error in getAssociateSubscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const findSubscriptionById = (products) => {
    const found = (products || []).find((prod) => prod.name === subscriptionName);
    if (found) {
      setSubscriptionId(found.subscriptionId);
    }
  };

  /**
   * Fetches the subscription expiration date.
   * @returns {string | null}
   */
  const getSubscriptionExpirationDate = () => {
    if (!subscriptionId) throw new Error('Subscription ID is not set');
    const subscription = (subscriptions || []).find(
      (sub) => sub.subscriptionId === subscriptionId
    );
    return subscription ? getMDYDate(subscription.expirationDate) : null;
  };
  // Generate or update service expiration date
  useEffect(() => {
    if (subscriptionId && subscriptions.length) {
      const exp = getSubscriptionExpirationDate();
      if (exp !== expirationDate) setExpirationDate(exp);
    }
  }, [subscriptionId, subscriptions]);

  // Isolate service by name and set id
  useEffect(() => {
    if (subscriptionName && customerId) {
      setLoading(true);
      if (subscriptions.length === 0 || refreshSubscriptions) {
        getProducts()
          .then((products) => {
            findSubscriptionById(products);
            setSubscriptions(products);
            setLoading(false);
          })
          .finally(() => setLoading(false));
      } else {
        findSubscriptionById(subscriptions || []);
        setLoading(false);
      }
    }
  }, [
    subscriptionName,
    subscriptionProducts,
    customerId,
    subscriptions.length,
    refreshSubscriptions,
  ]);

  // Fetch associated subscriptions
  useEffect(() => {
    // Make sure to not fetch if expired token or loading
    if (refreshSubscriptions) {
      getAssociateSubscription();
    }
  }, [refreshSubscriptions]);

  useEffect(() => {
    if (expirationDate) {
      const now = new Date();
      const expDate = new Date(expirationDate);
      const isExpiredNow = expDate < now;
      if (isExpired !== isExpiredNow) setIsExpired(isExpiredNow);
    }
  }, [isExpired, expirationDate]);

  return {
    loading,
    expirationDate,
    isExpired,
    subscriptions,
    setCustomerId,
    setSubscriptions,
    setErrorMsg,
    setSubscriptionName,
    setSubscriptionProducts,
  };
};

useSubscribeService.propTypes = {
  customerId: PropTypes.string,
  subscriptions: PropTypes.array,
};

export default useSubscribeService;
